import type { BaseTreeNode } from '../../tree';

export enum ComposerLogicalOperator {
    AND = 'AND',
    OR = 'OR'
}

export enum ComposerComparisonOperator {
    EQUAL = 'Equal',
    NOT_EQUAL = 'NotEqual',
    IN = 'In',
    NOT_IN = 'NotIn'
}

export enum ComposerAttributePreviewType {
    Rectangle = 1,
    Circle = 2
}

export enum ComposerOptionActionsType {
    SHOW_OBJECTS = 99, // MPlaza.SceneActionType.ShowHideMesh,
    HIDE_OBJECTS = 2, //MPlaza.SceneActionType.ShowHideMesh,
    SET_OBJECT_MATERIAL = 3, //MPlaza.SceneActionType.SetMeshMaterial,
    SET_SUB_OBJECT_MATERIAL = 7, //MPlaza.SceneActionType.SetSubMeshMaterial,
    SET_MATERIAL_COLOR = 4, //MPlaza.SceneActionType.SetMaterialColor,
    MOVE_CAMERA = 5, //MPlaza.SceneActionType.SetCameraLocation,
    SET_DESIGN_MATERIAL = 11, //MPlaza.SceneActionType.SetMeshDesignMaterial,
    SET_CAMERA_PIVOT = 8, //MPlaza.SceneActionType.SetCameraPivot,
    RESET_CAMERA_PIVOT = 9, //MPlaza.SceneActionType.ResetCameraPivot,
    SET_MATERIAL_PARAMETER = 17, // MPlaza.SceneActionType.SetMaterialParameter
    SET_PRINTING_METHOD = 18 // MPlaza.SceneActionType.SetPrintingMethod
}

// Enum per i tipi di proprietà dei materiali
export enum ComposerMaterialPropertyType {
    COLOR = 'color',
    TEXTURE = 'texture',
    NUMBER = 'number',
    BOOLEAN = 'boolean'
}

// Enum per i nomi delle proprietà dei materiali
export enum ComposerMaterialPropertyName {
    // Base/Albedo
    baseColor = 'baseColor',
    baseTexture = 'baseTexture',
    diffuseColor = 'diffuseColor',
    diffuseTexture = 'diffuseTexture',

    // Metallic
    metallicFactor = 'metallicFactor',
    metallicTexture = 'metallicTexture',
    metallicReflectanceColor = 'metallicReflectanceColor',
    metallicReflectanceTexture = 'metallicReflectanceTexture',

    // Roughness
    roughness = 'roughness',
    roughnessTexture = 'roughnessTexture',

    // Specular (materiali standard)
    specularColor = 'specularColor',
    specularTexture = 'specularTexture',
    specularPower = 'specularPower',

    // Emissive
    emissiveColor = 'emissiveColor',
    emissiveTexture = 'emissiveTexture',
    emissiveIntensity = 'emissiveIntensity',

    // Ambient
    ambientColor = 'ambientColor',
    ambientTexture = 'ambientTexture',

    // Opacity
    alpha = 'alpha',
    opacityTexture = 'opacityTexture',

    // Bump/Normal
    bumpTexture = 'bumpTexture',
    normalTexture = 'normalTexture',

    // Reflection
    reflectionColor = 'reflectionColor',
    reflectionTexture = 'reflectionTexture',
    reflectionFresnelParameters = 'reflectionFresnelParameters',

    // Index of Refraction
    indexOfRefraction = 'indexOfRefraction',

    // Clear Coat
    clearCoatIntensity = 'clearCoat.intensity',
    clearCoatRoughness = 'clearCoat.roughness',
    clearCoatIndexOfRefraction = 'clearCoat.indexOfRefraction',
    clearCoatTexture = 'clearCoat.texture',
    clearCoatTintColor = 'clearCoat.tintColor',

    // Sheen
    sheenIntensity = 'sheen.intensity',
    sheenColor = 'sheen.color',
    sheenRoughness = 'sheen.roughness',
    sheenTexture = 'sheen.texture',

    // Anisotropy
    anisotropyIntensity = 'anisotropy.intensity',
    anisotropyDirection = 'anisotropy.direction',
    anisotropyTexture = 'anisotropy.texture',

    // Subsurface
    subSurfaceIntensity = 'subSurfaceIntensity',
    subSurfaceTintColor = 'subSurfaceTintColor',
    subSurfaceTranslucencyColor = 'subSurfaceTranslucencyColor',
    subSurfaceDiffusionDistance = 'subSurfaceDiffusionDistance',
    subSurfaceTexture = 'subSurfaceTexture',

    // Flags
    wireframe = 'wireframe',
    pointsCloud = 'pointsCloud',
    backFaceCulling = 'backFaceCulling',
    separateCullingPass = 'separateCullingPass',
    useDepthPrePass = 'useDepthPrePass',
    useLogarithmicDepth = 'useLogarithmicDepth',
    twoSidedLighting = 'twoSidedLighting',
    unlit = 'unlit',

    // Intensity
    directIntensity = 'directIntensity',
    environmentIntensity = 'environmentIntensity',
    specularIntensity = 'specularIntensity',

    // Camera
    cameraPosition = 'cameraPosition',
    cameraTarget = 'cameraTarget'
}

// Funzioni helper per determinare il tipo di proprietà
export function getPropertyTypeFromName(propertyName: ComposerMaterialPropertyName): ComposerMaterialPropertyType {
    const name = propertyName.toString();
    if (name.includes('Color') || name === 'baseColor' || name === 'diffuseColor') {
        return ComposerMaterialPropertyType.COLOR;
    }
    if (name.includes('Texture') || name === 'baseTexture' || name === 'diffuseTexture') {
        return ComposerMaterialPropertyType.TEXTURE;
    }
    if (
        name.includes('wireframe') ||
        name.includes('unlit') ||
        name.includes('twoSided') ||
        name.includes('backFace') ||
        name.includes('use') ||
        name.includes('points')
    ) {
        return ComposerMaterialPropertyType.BOOLEAN;
    }
    return ComposerMaterialPropertyType.NUMBER;
}

export function getPropertiesByType(type: ComposerMaterialPropertyType): ComposerMaterialPropertyName[] {
    return Object.values(ComposerMaterialPropertyName).filter((prop) => getPropertyTypeFromName(prop) === type);
}

export const GetActionTypeFromValue = (type: MPlaza.SceneActionType, value: string): ComposerOptionActionsType => {
    switch (type) {
        case MPlaza.SceneActionType.ShowHideMesh:
            return value === 'hide' ? ComposerOptionActionsType.HIDE_OBJECTS : ComposerOptionActionsType.SHOW_OBJECTS;
        case MPlaza.SceneActionType.SetMeshMaterial:
            return ComposerOptionActionsType.SET_OBJECT_MATERIAL;
        case MPlaza.SceneActionType.SetSubMeshMaterial:
            return ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL;
        case MPlaza.SceneActionType.SetMaterialColor:
            return ComposerOptionActionsType.SET_MATERIAL_COLOR;
        case MPlaza.SceneActionType.SetMaterialParameter:
            return ComposerOptionActionsType.SET_MATERIAL_PARAMETER;
        case MPlaza.SceneActionType.SetPrintingMethod:
            return ComposerOptionActionsType.SET_PRINTING_METHOD;
        case MPlaza.SceneActionType.SetCameraLocation:
            return ComposerOptionActionsType.MOVE_CAMERA;
        case MPlaza.SceneActionType.SetMeshDesignMaterial:
            return ComposerOptionActionsType.SET_DESIGN_MATERIAL;
        case MPlaza.SceneActionType.SetCameraPivot:
            return ComposerOptionActionsType.SET_CAMERA_PIVOT;
        case MPlaza.SceneActionType.ResetCameraPivot:
            return ComposerOptionActionsType.RESET_CAMERA_PIVOT;
    }
    return ComposerOptionActionsType.SHOW_OBJECTS;
};

export const GetActionTypeFromAiType = (type: string): ComposerOptionActionsType => {
    switch (type.toLowerCase()) {
        case 'show_object':
            return ComposerOptionActionsType.SHOW_OBJECTS;
        case 'hide_object':
            return ComposerOptionActionsType.HIDE_OBJECTS;
        case 'set_object_material':
            return ComposerOptionActionsType.SET_OBJECT_MATERIAL;
        case 'set_sub_object_material':
            return ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL;
        case 'set_material_color':
            return ComposerOptionActionsType.SET_MATERIAL_COLOR;
        case 'move_camera':
            return ComposerOptionActionsType.MOVE_CAMERA;
        case 'set_design_material':
            return ComposerOptionActionsType.SET_DESIGN_MATERIAL;
        case 'set_camera_pivot':
            return ComposerOptionActionsType.SET_CAMERA_PIVOT;
        case 'reset_camera_pivot':
            return ComposerOptionActionsType.RESET_CAMERA_PIVOT;
        default:
            return ComposerOptionActionsType.SHOW_OBJECTS;
    }
};
export const GetActionValueFromType = (type: ComposerOptionActionsType): MPlaza.SceneActionType => {
    switch (type) {
        case ComposerOptionActionsType.SHOW_OBJECTS:
            return MPlaza.SceneActionType.ShowHideMesh;
        case ComposerOptionActionsType.HIDE_OBJECTS:
            return MPlaza.SceneActionType.ShowHideMesh;
        case ComposerOptionActionsType.SET_OBJECT_MATERIAL:
            return MPlaza.SceneActionType.SetMeshMaterial;
        case ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL:
            return MPlaza.SceneActionType.SetSubMeshMaterial;
        case ComposerOptionActionsType.SET_MATERIAL_COLOR:
            return MPlaza.SceneActionType.SetMaterialColor;
        case ComposerOptionActionsType.SET_MATERIAL_PARAMETER:
            return MPlaza.SceneActionType.SetMaterialParameter;
        case ComposerOptionActionsType.SET_PRINTING_METHOD:
            return MPlaza.SceneActionType.SetPrintingMethod;
        case ComposerOptionActionsType.MOVE_CAMERA:
            return MPlaza.SceneActionType.SetCameraLocation;
        case ComposerOptionActionsType.SET_DESIGN_MATERIAL:
            return MPlaza.SceneActionType.SetMeshDesignMaterial;
        case ComposerOptionActionsType.SET_CAMERA_PIVOT:
            return MPlaza.SceneActionType.SetCameraPivot;
        case ComposerOptionActionsType.RESET_CAMERA_PIVOT:
            return MPlaza.SceneActionType.ResetCameraPivot;
    }
    return MPlaza.SceneActionType.ShowHideMesh;
};
export enum ComposerMaterialsColorType {
    Diffuse = 'diffuseColor',
    Albedo = 'albedoColor',
    Base = 'baseColor',
    Ambient = 'ambientColor',
    Specular = 'specularColor',
    Emissive = 'emissiveColor',
    Reflection = 'reflectionColor'
}
export interface ComposerAttributeNodeLink {
    operation: ComposerComparisonOperator;
    attributeID: string;
    optionID: string;
}

interface ComposerPairValue {
    id: string;
    value: string;
}

export enum ComposerAttributeType {
    Attribute = 'Attribute',
    AttributeStep = 'AttributeStep',
    AttributeGroup = 'AttributeGroup',
    PreSetContainer = 'PreSetContainer'
}

export interface ComposerExtensionFieldNode {
    extensionFieldID: number;
    name: string;
    label: string;
    value: string;
    dataTypeID: number;
    operationTypeID: number;
    formatString: string;
}

export interface ComposerExtensionFieldDefinition {
    id?: string;
    fieldID: number;
    name: string;
    label: string;
    formatString: string;
    dataTypeID: number;
    dataTypeName?: string | null;
    operationTypeID: number;
    operationTypeName?: string | null;
}

export interface ComposerMerchantProductOptionValue {
    valueID: string;
    label: string;
}

export interface ComposerMerchantProductOption {
    optionID: string;
    label: string;
    values: ComposerMerchantProductOptionValue[];
}

export interface ComposerPrintTypeDefinition {
    printTypeID: number;
    name: string;
}

export interface ComposerPricingNode {
    attributePricingID: number;
    selectedOptionID: number;
    selectedOptionGuid: string;
    selectedValue: string;
    price: number;
    pricingID: number;
    name?: string;
}

export interface ComposerAttributeNode extends BaseTreeNode {
    idNumeric: number;
    code: string;
    description: string;
    type: ComposerAttributeType;
    displayOrder: number;
    enabled: boolean;
    parentID?: string;
    parentIDNumeric: number;
    parentType?: ComposerAttributeType;
    isNew?: boolean;
    expanded?: boolean;
    img?: string;
    previewType?: ComposerAttributePreviewType;
    hideOptionsLabel?: boolean;
    camera?: string;
    objects?: string;
    direction?: number;
    attributesAlwaysOpened?: boolean;
    platformAttribute?: ComposerEcommerceAttribute;
    options: ComposerAttributeOptionNode[];
    pricings: ComposerPricingNode[];
    linkOperation?: ComposerLogicalOperator;
    links: ComposerAttributeNodeLink[];

    brokenValues: Map<string, string>;
}

export interface ComposerAttributeOptionNode extends BaseTreeNode {
    idNumeric: number;
    code: string;
    description: string;
    attributeID: string;
    displayOrder: number;
    enabled: boolean;
    actions: ComposerOptionActionNode[];
    extensionFields: ComposerExtensionFieldNode[];
    isDefault?: boolean;
    isNew?: boolean;
    img?: string;
    platformAttribute?: ComposerEcommerceAttribute;
    linkOperation?: ComposerLogicalOperator;
    links: ComposerAttributeNodeLink[];

    brokenValues: Map<string, string>;
}

export interface ComposerOptionActionNode extends BaseTreeNode {
    displayOrder: number;
    enabled: boolean;
    attributeID: string;
    optionID: string;
    type: ComposerOptionActionsType;
    sceneElementType: MPlaza.SceneElementType;
    selector: string;
    property?: string;
    value: string;
    isNew?: boolean;
    brokenValues: Map<string, string>;
}

export interface ComposerDesignAreaItem {
    areaID: number;
    colorID: number;
    id: string;
    modelID: number;
    sideID: number;
}
export interface ComposerTemplateSideRestrictions {
    userCanAddTexts?: boolean;
    userCanAddImages?: boolean;
    isGalleryEnabled?: boolean;
    userCanAddUploadImages?: boolean;
    jpgUploadEnabled?: boolean;
    pngUploadEnabled?: boolean;
    svgUploadEnabled?: boolean;
    pdfUploadEnabled?: boolean;
    pdfWithRasterizationEnabled?: boolean;
    epsUploadEnabled?: boolean;
}
export interface ComposerTemplateNode extends BaseTreeNode {
    templateID: number;
    designDocID: string;

    restrictions: Map<number, ComposerTemplateSideRestrictions>; // key: sideID
    design: MPlaza.Design;
    expanded: boolean;
}
export interface ComposerAreaNode extends BaseTreeNode {
    sideID: number;
    ppcm: number;
    objects: string;
    width: number;
    height: number;
    camera: string;
    uvMap: number;
    customTextureResolution: boolean;
    desktopTextureResolution?: number;
    mobileTextureResolution?: number;
    originalSide: MPlaza.Side;
    originalMeshSides?: MPlaza.ModelSceneMeshSide[];
    templateID: string;
}
export enum ComposerEcommercePlatform {
    None = -1,
    Magento1 = 5,
    Magento2 = 1,
    Shopify = 3,
    WooCommerce = 2,
    API = 7,
    CCloud = 11,
    Netsuite = 10,
    Prestashop = 6,
    BigCommerce = 9,
    OpenCart = 4,
    Etsy = 12,
    Shopware = 14
}

export interface ComposerEcommerceSettings {
    ecommercePlatform: ComposerEcommercePlatform;
}

export interface ComposerEcommerceAttribute {
    id: string;
    label: string;
    rawAttribute?: ComposerAttributeNode;
}

export interface AttributePreviewItems {
    name: string;
    selected: boolean;
    options: AttributePreviewOption[];
}
export interface AttributePreviewOption {
    name: string;
    open?: boolean;
    actions: AttributePreviewAction[];
}

export interface AttributePreviewAction {
    type: string;
    objects: string[];
    objectLabels?: string[];
    material: string;
    materialLabel?: string;
}
